import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { isIOS, isMacOs } from "react-device-detect";
import {
  ButtonActionType,
  FieldType,
  FormField,
  FormPostSubmitType,
  FormSettings,
  OptionType,
  TextObject,
} from "@/shared/types";

declare module "yup" {
  interface StringSchema {
    phone(): StringSchema;
    time(): StringSchema;
    requiredWhen(when: boolean): StringSchema;
  }
  // @ts-ignore
  interface ArraySchema<T> {
    // @ts-ignore
    requiredArray(when: boolean): ArraySchema<T>;
  }
}

const phoneRegExp =
  /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

yup.addMethod<any>(yup.string, "phone", function () {
  return this.test("phone", "Phone number is not valid", (value: string) => {
    if (!value) return true;
    return phoneRegExp.test(value);
  });
});

yup.addMethod<any>(yup.string, "requiredWhen", function (when: boolean) {
  return this.test(
    "requiredWhen",
    "This field is required",
    (value: string) => {
      if (!when) return true;
      return !!value?.trim?.();
    }
  );
});

yup.addMethod<any>(yup.string, "time", function () {
  return this.test("time", "number is required", (value: string) => {
    return !value?.includes("undefined");
  });
});

yup.addMethod<any>(yup.array, "requiredArray", function (when: boolean) {
  return this.test("requiredArray", "Array is empty", (value: string) => {
    if (!when) return true;
    return value?.length! > 0;
  });
});

const Validator = (settings: any) => {
  const { required, isMultipleSelection } = settings;
  const selectedOptionSchema = yup.object().shape({
    label: yup.string().requiredWhen(required),
    value: yup.string().requiredWhen(required),
  });

  const checkboxSchema = required && yup.array().requiredArray(required);
  const selectSchema = isMultipleSelection
    ? yup.array(selectedOptionSchema).requiredArray(required)
    : selectedOptionSchema;

  return {
    [FieldType.Text]: yup.string().requiredWhen(required),
    [FieldType.Link]: yup.string().url().requiredWhen(required),
    [FieldType.Email]: yup.string().email().requiredWhen(required),
    [FieldType.TextArea]: yup.string().requiredWhen(required),
    [FieldType.Phone]: yup.string().phone().requiredWhen(required),
    [FieldType.Checkbox]: checkboxSchema,
    [FieldType.Select]: selectSchema,
    [FieldType.File]: yup.string().requiredWhen(required),
    [FieldType.Radio]: yup.string().requiredWhen(required),
    [FieldType.Date]: yup.string().requiredWhen(required),
    [FieldType.Time]: yup.string().time().requiredWhen(required),
  } as any;
};

export function getFormResolver(formSettings: FormSettings) {
  const validationSchema = formSettings?.fields?.reduce(
    (acc, curr) => ({
      ...acc,
      [curr?.id]: Validator(curr?.settings)[curr?.type!],
    }),
    {}
  );
  const yupSchema = yup.object(validationSchema);

  return yupResolver(yupSchema);
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  const yyyy = date.getFullYear().toString();
  const mm = (date.getMonth() + 1).toString();
  const dd = date.getDate().toString();

  const mmChars = mm.split("");
  const ddChars = dd.split("");

  if (dateStr) {
    return (
      yyyy +
      "/" +
      (mmChars[1] ? mm : "0" + mmChars[0]) +
      "/" +
      (ddChars[1] ? dd : "0" + ddChars[0])
    );
  } else {
    return "";
  }
}

function getFieldValue(field: FormField, value: any, text: TextObject) {
  if (field?.type === FieldType.Checkbox) {
    const optionsValues = field?.settings?.options
      ?.filter((option: OptionType) => (value || [])?.includes(option?.id!))
      ?.map((option: OptionType) => text[option?.label?.textId!]);
    return optionsValues;
  }

  if (field?.type === FieldType.Date) {
    return formatDate(value);
  }

  if (field?.type === FieldType.Time) {
    return value ? value : "";
  }

  if (field?.type === FieldType.Select) {
    const newValue = !value ? [] : Array.isArray(value) ? value : [value];
    const optionsValues = newValue?.map((option: OptionType) => option?.label);
    return optionsValues;
  }

  return value || "";
}

export function getFormContent(
  formSettings: FormSettings,
  formValues: { [key: string]: string },
  text: TextObject
) {
  const formContent = formSettings?.fields?.map((field) => {
    const value = formValues[field?.id];
    const fieldValue = getFieldValue(field, value, text);
    return {
      type: field?.type,
      required: !!field?.settings?.required,
      value: fieldValue,
      field_name:
        text[field?.settings?.label?.textId!] ||
        text[field?.settings?.placeholder?.textId!] ||
        "",
    };
  });
  return formContent;
}

export function handlePostSubmitRedirect(
  formSettings: FormSettings,
  router: any
) {
  const postSubmitType = formSettings?.formPostSubmit?.submitType;
  const redirectType = formSettings?.formPostSubmit?.redirectType;
  const shouldOpenInANewTab = formSettings?.formPostSubmit?.shouldOpenInANewTab;
  const linkTarget = shouldOpenInANewTab ? "_blank" : "_self";
  const redirectLocation = isIOS || isMacOs ? "_self" : linkTarget;
  const externalLink = formSettings?.formPostSubmit?.externalLink;
  const internalPageId = formSettings?.formPostSubmit?.internalLink;

  if (typeof window === "undefined") return;
  if (postSubmitType !== FormPostSubmitType.Redirect) return;

  if (redirectType === ButtonActionType.ExternalLink && externalLink) {
    window.parent.open(externalLink, redirectLocation);
  }
  if (redirectType === ButtonActionType.InternalLink && internalPageId) {
    if (!router) return;
    router.pushPage(internalPageId, redirectLocation);
  }
}

export async function uploadFileMutation(
  uploadFileUrl: (formId?: string, sectionId?: string) => string,
  file: File,
  fieldId: string,
  formId: string,
  sectionId: string,
  sectionValueId: string,
  elementId: string
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("field_id", fieldId.split(":")[1]);
  formData.append("section_value_id", sectionValueId);
  elementId && formData.append("element_id", elementId.split(":")[1]);

  const res = await fetch(uploadFileUrl(formId, sectionId), {
    method: "POST",
    body: formData,
  });
  if (res.status === 429)
    alert(
      "You have reached the limit of uploading files, Please try again later after 24 hours."
    );
  const data = await res.json();

  return data;
}
